import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { ChevronsRight, Pause, Play } from 'lucide-react';
import { useSessionRecordingData } from '../hooks/useSessionRecordings';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface SessionRecordingPlayerProps {
  deviceId: string;
  recordingId: string;
  durationSeconds: number | null;
}

interface AsciicastHeader {
  width?: number;
  height?: number;
  [key: string]: unknown;
}

interface AsciicastEvent {
  timestamp: number;
  type: string;
  data: string;
}

function parseAsciicast(raw: string): { header: AsciicastHeader; events: AsciicastEvent[] } {
  const lines = raw.trim().split('\n');
  if (lines.length === 0) {
    return { header: {}, events: [] };
  }

  const header = JSON.parse(lines[0]) as AsciicastHeader;
  const events: AsciicastEvent[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const parsed = JSON.parse(line) as [number, string, string];
    events.push({
      timestamp: parsed[0],
      type: parsed[1],
      data: parsed[2],
    });
  }

  return { header, events };
}

/** The size a recording's terminal opened at, from its header. */
function initialSize(header: AsciicastHeader): [number, number] {
  return [
    typeof header.width === 'number' ? header.width : 120,
    typeof header.height === 'number' ? header.height : 24,
  ];
}

/**
 * Play one event: output is written, and a resize (`"r"`, data `COLSxROWS`)
 * resizes the terminal, as the session's did at that moment.
 */
function playEvent(terminal: Terminal, event: AsciicastEvent) {
  if (event.type === 'o') {
    terminal.write(event.data);
    return;
  }
  const size = /^(\d+)x(\d+)$/.exec(event.data);
  if (size) terminal.resize(Number(size[1]), Number(size[2]));
}

// Inline styles for range input theming (cross-browser)
const scrubberStyles = `
  .asciicast-scrubber {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    margin: 0;
  }
  .asciicast-scrubber::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 4px;
    background: transparent;
  }
  .asciicast-scrubber::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #89b4fa;
    border: 2px solid #cdd6f4;
    cursor: pointer;
    margin-top: -3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.4);
    transition: transform 0.1s;
  }
  .asciicast-scrubber::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    background: #b4d0fb;
  }
  .asciicast-scrubber::-webkit-slider-thumb:active {
    transform: scale(1.3);
    background: #cdd6f4;
  }
  .asciicast-scrubber::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: transparent;
  }
  .asciicast-scrubber::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #89b4fa;
    border: 2px solid #cdd6f4;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0,0,0,0.4);
    transition: transform 0.1s;
  }
  .asciicast-scrubber::-moz-range-thumb:hover {
    transform: scale(1.2);
    background: #b4d0fb;
  }
  .asciicast-scrubber::-moz-range-progress {
    background: #89b4fa;
    height: 8px;
    border-radius: 4px 0 0 4px;
  }
`;

export default function SessionRecordingPlayer({ deviceId, recordingId, durationSeconds }: SessionRecordingPlayerProps) {
  const { data: asciicastData, isLoading, isError } = useSessionRecordingData(deviceId, recordingId);
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const eventsRef = useRef<AsciicastEvent[]>([]);
  const headerRef = useRef<AsciicastHeader>({});

  // Playback engine state (refs to avoid re-renders in rAF loop)
  const rafIdRef = useRef<number>(0);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const playbackStartWallRef = useRef(0);   // Date.now() when playback started/resumed
  const playbackOffsetRef = useRef(0);       // virtual time offset (seconds) for seek support
  const eventIndexRef = useRef(0);           // next played event index to process
  const playedEventsRef = useRef<AsciicastEvent[]>([]); // output and resizes

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  // Parsed once per recording; the duration is the last event's timestamp.
  const parsed = useMemo(() => (asciicastData ? parseAsciicast(asciicastData) : null), [asciicastData]);
  const totalDuration = parsed?.events.at(-1)?.timestamp ?? 0;
  const [isScrubbing, setIsScrubbing] = useState(false);

  // Initialize terminal — depends on isLoading/isError because the container
  // div is only in the DOM when neither early-return branch is taken.
  useEffect(() => {
    if (isLoading || isError || !containerRef.current) return;

    const terminal = new Terminal({
      cursorBlink: false,
      cols: 120,
      rows: 24,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Menlo, Monaco, 'Courier New', monospace",
      disableStdin: true,
      scrollback: 5000,
      theme: {
        background: '#1e1e2e',
        foreground: '#cdd6f4',
        cursor: '#1e1e2e',
        selectionBackground: '#585b70',
        black: '#45475a',
        red: '#f38ba8',
        green: '#a6e3a1',
        yellow: '#f9e2af',
        blue: '#89b4fa',
        magenta: '#f5c2e7',
        cyan: '#94e2d5',
        white: '#bac2de',
        brightBlack: '#585b70',
        brightRed: '#f38ba8',
        brightGreen: '#a6e3a1',
        brightYellow: '#f9e2af',
        brightBlue: '#89b4fa',
        brightMagenta: '#f5c2e7',
        brightCyan: '#94e2d5',
        brightWhite: '#a6adc8',
      },
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(containerRef.current);

    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      terminal.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  }, [isLoading, isError]);

  // Replay all output events up to a given virtual time, instantly
  const replayUpTo = useCallback((targetTime: number) => {
    const terminal = terminalRef.current;
    const playedEvents = playedEventsRef.current;
    if (!terminal) return;

    terminal.reset();
    // From the size it opened at: any resize before targetTime replays below.
    terminal.resize(...initialSize(headerRef.current));
    let idx = 0;
    for (; idx < playedEvents.length; idx++) {
      if (playedEvents[idx].timestamp > targetTime) break;
      playEvent(terminal, playedEvents[idx]);
    }
    eventIndexRef.current = idx;
  }, []);

  // The rAF playback loop
  const tick = useCallback(() => {
    if (!playingRef.current) return;

    const elapsed = (Date.now() - playbackStartWallRef.current) / 1000;
    const virtualTime = playbackOffsetRef.current + elapsed * speedRef.current;
    const duration = totalDuration;

    // Write any output events that are due
    const playedEvents = playedEventsRef.current;
    const terminal = terminalRef.current;
    let idx = eventIndexRef.current;
    while (idx < playedEvents.length && playedEvents[idx].timestamp <= virtualTime) {
      if (terminal) playEvent(terminal, playedEvents[idx]);
      idx++;
    }
    eventIndexRef.current = idx;

    // Update UI state (throttled by rAF — ~60fps)
    const clampedTime = Math.min(virtualTime, duration);
    setCurrentTime(clampedTime);

    if (virtualTime >= duration) {
      // Playback finished
      playingRef.current = false;
      setIsPlaying(false);
      setCurrentTime(duration);
      return;
    }

    rafIdRef.current = requestAnimationFrame(tick);
  }, [totalDuration]);

  // Start or resume playback from a given virtual time
  const startFrom = useCallback((fromTime: number, speed: number) => {
    cancelAnimationFrame(rafIdRef.current);

    speedRef.current = speed;
    playbackOffsetRef.current = fromTime;
    playbackStartWallRef.current = Date.now();
    playingRef.current = true;
    setIsPlaying(true);

    rafIdRef.current = requestAnimationFrame(tick);
  }, [tick]);

  // Parse data and auto-play when asciicast data loads
  useEffect(() => {
    if (!parsed) return;

    const { header, events } = parsed;
    headerRef.current = header;
    eventsRef.current = events;

    const playedEvents = events.filter(e => e.type === 'o' || e.type === 'r');
    playedEventsRef.current = playedEvents;

    // Start at the size the session opened at; its resizes play as events.
    const terminal = terminalRef.current;
    terminal?.resize(...initialSize(header));

    if (playedEvents.length > 0) {
      eventIndexRef.current = 0;
      terminal?.reset();
      // Start on the next frame, like every later frame of the loop; the cleanup
      // below cancels it if the recording changes first.
      rafIdRef.current = requestAnimationFrame(() => {
        startFrom(0, speedRef.current);
      });
    }

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      playingRef.current = false;
    };
    // Only run when the recording changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed]);

  const handlePlayPause = () => {
    if (isPlaying) {
      // Pause: stop the rAF loop, freeze at current time
      cancelAnimationFrame(rafIdRef.current);
      playingRef.current = false;
      setIsPlaying(false);
    } else {
      // Resume from current position (or restart if at end)
      const resumeTime = currentTime >= totalDuration ? 0 : currentTime;
      if (resumeTime === 0) {
        replayUpTo(0);
      }
      startFrom(resumeTime, playbackSpeed);
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    speedRef.current = speed;
    if (playingRef.current) {
      // Adjust without replaying: freeze current virtual time, restart with new speed
      const elapsed = (Date.now() - playbackStartWallRef.current) / 1000;
      const virtualTime = playbackOffsetRef.current + elapsed * (speedRef.current);
      // Recalculate with new speed — we need to update the offset and wall start
      // so the rAF loop picks up seamlessly
      playbackOffsetRef.current = virtualTime;
      playbackStartWallRef.current = Date.now();
      speedRef.current = speed;
    }
  };

  const handleSkipToEnd = () => {
    cancelAnimationFrame(rafIdRef.current);
    playingRef.current = false;
    replayUpTo(totalDuration);
    setIsPlaying(false);
    setCurrentTime(totalDuration);
  };

  // Scrubber handlers
  const handleScrubStart = () => {
    setIsScrubbing(true);
    // Pause playback while scrubbing
    if (playingRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      playingRef.current = false;
    }
  };

  const handleScrubChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seekTime = Number(e.target.value);
    replayUpTo(seekTime);
    setCurrentTime(seekTime);
  };

  const handleScrubEnd = (e: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>) => {
    setIsScrubbing(false);
    const seekTime = Number((e.target as HTMLInputElement).value);
    // Resume playback from the scrub position if we were playing
    if (isPlaying || seekTime < totalDuration) {
      startFrom(seekTime, playbackSpeed);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const progressPercent = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-[#1e1e2e] p-8 text-center">
        <Progress value={undefined} className="h-1 max-w-xs mx-auto" />
        <p className="mt-2 text-sm text-[#cdd6f4]">Loading recording…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" className="text-xs">
        <AlertDescription>Failed to load recording data.</AlertDescription>
      </Alert>
    );
  }

  // Prefer event-derived duration over API's durationSeconds, which may
  // include idle timeout periods at the end of the session.
  const displayDuration = totalDuration > 0 ? totalDuration : (durationSeconds ?? 0);

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      <style>{scrubberStyles}</style>

      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-[#181825] border-b border-[#313244]">
        <div className="inline-flex rounded-md overflow-hidden">
          <button
            type="button"
            className="inline-flex items-center justify-center bg-[#313244] text-[#cdd6f4] px-2 py-1 text-xs hover:bg-[#45475a] border-r border-[#1e1e2e]"
            onClick={handlePlayPause}
            title={isPlaying ? 'Pause' : 'Play'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center bg-[#313244] text-[#cdd6f4] px-2 py-1 text-xs hover:bg-[#45475a]"
            onClick={handleSkipToEnd}
            title="Skip to end"
            aria-label="Skip to end"
          >
            <ChevronsRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-[100px] relative">
          <div
            className="absolute top-1/2 left-0 right-0 h-2 -mt-1 rounded bg-[#313244] overflow-hidden pointer-events-none"
          >
            <div
              className={cn(
                'h-full bg-[#89b4fa] rounded-l',
                isScrubbing ? '' : 'transition-[width] duration-75',
              )}
              style={{ width: `${String(progressPercent)}%` }}
            />
          </div>
          <input
            type="range"
            className="asciicast-scrubber relative z-10 bg-transparent"
            min={0}
            max={totalDuration}
            step={0.01}
            value={currentTime}
            onMouseDown={handleScrubStart}
            onTouchStart={handleScrubStart}
            onChange={handleScrubChange}
            onMouseUp={handleScrubEnd}
            onTouchEnd={handleScrubEnd}
          />
        </div>

        <span className="text-[#6c7086] text-xs font-mono whitespace-nowrap">
          {formatTime(currentTime)} / {formatTime(displayDuration)}
        </span>

        <select
          value={playbackSpeed}
          onChange={(e) => { handleSpeedChange(Number(e.target.value)); }}
          className="bg-[#313244] text-[#cdd6f4] border border-[#45475a] rounded px-2 py-1 text-xs"
        >
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
          <option value={2}>2x</option>
          <option value={5}>5x</option>
          <option value={10}>10x</option>
        </select>
      </div>

      <div ref={containerRef} className="bg-[#1e1e2e] p-1 overflow-x-auto" />
    </div>
  );
}
